import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageMeta from "@/components/base/PageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareModal from "@/components/base/ShareModal";
import EmptyState from "@/components/base/EmptyState";
import { news } from "@/mocks/news";
import { formatDate } from "@/utils/format";
import type { NewsArticle } from "@/types";

export default function NewsDetail() {
  const { id } = useParams();
  const [shareOpen, setShareOpen] = useState(false);

  const allNews = news as NewsArticle[];
  const article = allNews.find((item) => item.id === id);

  const related = useMemo(() => {
    if (!article) return [];
    return allNews
      .filter((item) => item.id !== article.id)
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .slice(0, 3);
  }, [allNews, article]);

  if (!article) {
    return (
      <div className="pt-16 md:pt-[72px] px-5 md:px-10 py-20">
        <EmptyState
          icon="ri-file-unknow-line"
          title="未找到该动态"
          description="你访问的动态可能已被移除或链接有误，返回动态列表继续浏览吧。"
          actionLabel="返回动态列表"
          onAction={() => window.history.back()}
        />
        <div className="flex justify-center mt-6">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-accent-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            返回明星动态列表
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-[72px]">
      <PageMeta
        title={`${article.title} - 佐山爱明星动态详情`}
        description={article.summary}
        keywords={`佐山爱,${article.tags.join(",")},明星动态,影视资讯`}
      />

      <article className="px-5 md:px-10 pt-10 md:pt-14 pb-10">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "明星动态", to: "/news" },
                { label: "正文" },
              ]}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap mb-4">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-md text-[11px] bg-secondary-100 text-secondary-900"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-snug mb-5">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500 pb-6 border-b border-background-200">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {formatDate(article.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 text-primary-700 hover:text-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-share-forward-line" />
              分享
            </button>
          </div>

          <div className="mt-8">
            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden mb-8 bg-background-100">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 佐山爱明星动态`}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="flex flex-col gap-5">
              {article.content.map((paragraph, i) => (
                <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          <div className="mt-10 flex items-center justify-between gap-4 flex-wrap">
            <Link
              to="/news"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-accent-600 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              返回明星动态列表
            </Link>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <i className="ri-share-forward-line" />
              分享这条动态
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="px-5 md:px-10 py-10 md:py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <div className="mb-8">
              <span className="text-xs font-medium tracking-[0.2em] uppercase text-accent-600">
                Related News
              </span>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mt-2">
                相关动态推荐
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group rounded-lg overflow-hidden bg-background-50 border border-background-200/70 hover:border-secondary-300 transition-all duration-300"
                >
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={item.thumbnail}
                      alt={item.title}
                      title={`${item.title} - 佐山爱明星动态`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading font-semibold text-sm text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-700 transition-colors mb-2">
                      {item.title}
                    </h3>
                    <span className="text-xs text-foreground-400">{formatDate(item.date)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </div>
  );
}